/**
 * §17 — Analytical overlays a coach toggles on top of the pitch.
 *
 * MVP scope: **Voronoi cells** — spatial dominance regions per player,
 * one polygon per input point, clipped to the pitch rectangle.
 *
 * Stretch overlays (heatmap, passNet, pitchControl) only get a state
 * shape + toggle plumbing in this module — the renderer ships a
 * "coming soon" placeholder until the underlying analytics land.
 *
 * Algorithm — bounded Voronoi via grid-sampling:
 *   1. Tile the pitch (length × width metres) with a 1 m grid.
 *   2. For every sample cell centre, find the nearest input point
 *      (squared-distance loop — no early-exit, just a linear sweep
 *      since N ≤ ~22 and the inner loop is trivially branch-predictable).
 *   3. Bucket sample cells by nearest-point index.
 *   4. For each bucket, walk the bucket's boundary cells and emit a
 *      simple polygon via a perimeter-trace (axis-aligned grid edges
 *      collapsed into colinear runs to keep the vertex count low).
 *
 * Approximations / caveats:
 *  - Cell boundaries are axis-aligned at the chosen grid resolution
 *    (1 m). Real Voronoi cells have straight diagonal edges; the
 *    visual difference at this scale is a slight "staircase" on the
 *    cell boundaries which is acceptable for an analytical overlay
 *    where exact geometry is not load-bearing.
 *  - Points outside the pitch are still allowed (the renderer caller
 *    is responsible for keeping spots inside), but only sample cells
 *    inside the pitch contribute to a cell, so an out-of-bounds point
 *    simply gets a smaller polygon than its tactical region.
 *  - Two coincident points are not de-duplicated — the second one
 *    will get a degenerate (zero-area) cell because its bucket is
 *    empty. Renderer should drop empty `vertices` arrays defensively.
 *  - Performance: 22 points × 105×68 grid = ~7,000 cells × 22 dist
 *    checks = ~156k ops. Measured locally well under 50 ms; if a
 *    consumer needs a tighter budget, drop to a 2 m grid step.
 */

// ---------------------------------------------------------------------------
// Overlay kinds + state
// ---------------------------------------------------------------------------

export const OVERLAY_KINDS = [
  'voronoi',
  'heatmap',
  'passNet',
  'pitchControl',
  'offsideLine',
  'defensiveShape',
  // Sky-MNF half-spaces — five vertical channels (LW / left half-space
  // / centre / right half-space / RW). Pure pitch markup, no compute
  // needed; renderer draws four channel dividers across the field.
  'halfSpaces',
  // Sky-MNF "horizontal lines" — connect each team's players within
  // a Z-band (back / mid / front) with a polyline so the team's shape
  // reads at a glance.
  'teamShape',
  // Juego de posición grid — extends half-spaces with two horizontal
  // lines splitting the pitch into thirds, plus the canonical zone-14
  // rectangle highlighted in the centre attacking third.
  'posPlayGrid',
  // Karun Singh-style Expected Threat reference grid — 12×16 zone
  // probabilities, cool→hot ramp. Static field independent of
  // current player positions; coaches use it to read "where the
  // ball is worth a lot vs little."
  'xt',
] as const;

export type OverlayKind = (typeof OVERLAY_KINDS)[number];

/** Default opacity for every overlay (0.5). */
const DEFAULT_OPACITY = 0.5;

/** Toggle + opacity state for every overlay kind. Saved against
 *  `TacticsLayout.overlays`. */
export interface TacticsOverlaysState {
  /** Currently visible overlay kinds. Order is preserved as the coach
   *  toggles them so the renderer can stack them deterministically. */
  active: OverlayKind[];
  /** Per-kind opacity in [0, 1]. Always populated for every kind so the
   *  UI can render an opacity slider without a fallback branch. */
  opacity: Record<OverlayKind, number>;
}

/** Fresh state — no overlays active, every opacity at 0.5. */
export function defaultOverlaysState(): TacticsOverlaysState {
  const opacity = {} as Record<OverlayKind, number>;
  for (const k of OVERLAY_KINDS) opacity[k] = DEFAULT_OPACITY;
  return { active: [], opacity };
}

/** Toggle an overlay on or off. Returns a NEW state object. */
export function toggleOverlay(
  state: TacticsOverlaysState,
  kind: OverlayKind,
): TacticsOverlaysState {
  const isActive = state.active.includes(kind);
  const nextActive = isActive
    ? state.active.filter((k) => k !== kind)
    : [...state.active, kind];
  return { ...state, active: nextActive };
}

/** Clamp helper — keeps opacity in [0, 1] regardless of caller input. */
function clampOpacity(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_OPACITY;
  if (value < 0) return 0;
  if (value > 1) return 1;
  return value;
}

/** Set the opacity for one overlay kind. Clamped to [0, 1]. Returns a
 *  NEW state object. */
export function setOverlayOpacity(
  state: TacticsOverlaysState,
  kind: OverlayKind,
  value: number,
): TacticsOverlaysState {
  return {
    ...state,
    opacity: { ...state.opacity, [kind]: clampOpacity(value) },
  };
}

/** Narrow an unknown value (e.g. read from localStorage) to a
 *  {@link TacticsOverlaysState}. Strict — requires every overlay kind
 *  to have a numeric opacity entry. */
export function isTacticsOverlaysState(
  value: unknown,
): value is TacticsOverlaysState {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  if (!Array.isArray(v.active)) return false;
  for (const k of v.active) {
    if (typeof k !== 'string') return false;
    if (!OVERLAY_KINDS.includes(k as OverlayKind)) return false;
  }
  if (!v.opacity || typeof v.opacity !== 'object') return false;
  const op = v.opacity as Record<string, unknown>;
  for (const k of OVERLAY_KINDS) {
    const o = op[k];
    if (typeof o !== 'number' || !Number.isFinite(o)) return false;
  }
  return true;
}

// ---------------------------------------------------------------------------
// Voronoi
// ---------------------------------------------------------------------------

/** Input to the Voronoi computation. Coordinates are world metres,
 *  pitch-centred (matches `TacticsPlayerSpot`). */
export interface VoronoiPoint {
  x: number;
  z: number;
  team?: 'home' | 'away';
}

/** One Voronoi cell — a polygon of vertices in world metres, plus the
 *  team tag carried over from the input point so the renderer can
 *  apply the home/away fill. */
export interface VoronoiCell {
  team?: 'home' | 'away';
  vertices: { x: number; z: number }[];
}

/** Grid step in metres. 1 m gives ~7k cells on a standard pitch with
 *  ~50 ms worst-case at N=22 — well inside the budget. */
const GRID_STEP_M = 1;

/**
 * Compute bounded Voronoi cells for the input points, clipped to the
 * pitch rectangle of `pitchLengthM × pitchWidthM` (centred on origin).
 *
 * One cell is emitted per input point, in the same order as the
 * input. Cells with no sample-grid coverage (e.g. coincident with
 * another point so their bucket is empty) get an empty `vertices`
 * array — the renderer should drop those.
 *
 * Coordinate convention matches `TacticsPlayerSpot`:
 *   x ∈ [-pitchWidthM/2,  +pitchWidthM/2]
 *   z ∈ [-pitchLengthM/2, +pitchLengthM/2]
 */
export function computeVoronoiCells(
  points: VoronoiPoint[],
  pitchLengthM: number,
  pitchWidthM: number,
): VoronoiCell[] {
  if (points.length === 0) return [];

  const halfL = pitchLengthM / 2;
  const halfW = pitchWidthM / 2;

  // Grid resolution — cell centres run from (-half + step/2) up to
  // (+half - step/2) so every centre lies inside the pitch.
  const step = GRID_STEP_M;
  // Number of grid cells along each axis. Round to keep the grid
  // bounded; the last cell may be slightly smaller than `step` but
  // the rasterisation tolerates that.
  const nCols = Math.max(1, Math.round(pitchLengthM / step)); // along z
  const nRows = Math.max(1, Math.round(pitchWidthM / step));  // along x

  // ownership[r * nCols + c] = index into `points` for that sample
  // cell's nearest input point. -1 means unassigned (shouldn't happen
  // once we have ≥1 point, but kept as a safety sentinel).
  const ownership = new Int32Array(nRows * nCols);
  ownership.fill(-1);

  for (let r = 0; r < nRows; r++) {
    // Sample-cell centre along x (width axis).
    const cx = -halfW + (r + 0.5) * step;
    for (let c = 0; c < nCols; c++) {
      // Sample-cell centre along z (length axis). Indexing convention:
      // c=0 is at the lowest z (-halfL), c=nCols-1 is at +halfL.
      const cz = -halfL + (c + 0.5) * step;
      let bestI = 0;
      let bestD = Infinity;
      for (let i = 0; i < points.length; i++) {
        const dx = points[i]!.x - cx;
        const dz = points[i]!.z - cz;
        const d = dx * dx + dz * dz;
        if (d < bestD) {
          bestD = d;
          bestI = i;
        }
      }
      ownership[r * nCols + c] = bestI;
    }
  }

  // For each input point, walk its sample-cell bucket to emit a simple
  // polygon. We compute the axis-aligned bounding box per point then
  // trace the perimeter using a convex-hull-of-boundary-points step.
  const cells: VoronoiCell[] = [];
  for (let i = 0; i < points.length; i++) {
    const vertices = traceCellPolygon(ownership, i, nRows, nCols, step, halfL, halfW);
    cells.push({ team: points[i]!.team, vertices });
  }
  return cells;
}

/** One line segment along the home/away contact line. World metres. */
export interface VoronoiFrontierSegment {
  a: { x: number; z: number };
  b: { x: number; z: number };
}

/** One horizontal "band" of players (back / mid / front) for the
 *  team-shape overlay. Points are sorted left-to-right (ascending x). */
export interface TeamShapeBand {
  team: 'home' | 'away';
  /** Polyline points in world metres, ordered for rendering. */
  points: { x: number; z: number }[];
}

/**
 * Cluster each team's outfielders into Z-bands (back / mid / front)
 * and emit a polyline per band.
 *
 * Approach: split the pitch length into three equal bands by Z, drop
 * each player into whichever band contains their z-coord, then sort
 * each band left-to-right by x. Bands with < 2 players are dropped
 * (a 1-point polyline is just a dot).
 *
 * The function intentionally doesn't exclude the goalkeeper — for
 * standard formations the GK lands in the back band naturally and a
 * 5-point back line still reads as a coherent shape. If a custom
 * formation puts the GK alone in an empty band it'll just drop out
 * via the 2-player minimum.
 */
export function computeTeamShapeBands(
  points: VoronoiPoint[],
  pitchLengthM: number,
): TeamShapeBand[] {
  if (points.length === 0) return [];
  const halfL = pitchLengthM / 2;
  const bandHeight = pitchLengthM / 3;

  const bandFor = (z: number): 0 | 1 | 2 => {
    if (z < -halfL + bandHeight) return 0;
    if (z < -halfL + bandHeight * 2) return 1;
    return 2;
  };

  // Bucket each team's points into 3 z-bands.
  const buckets: Record<'home' | 'away', Array<Array<{ x: number; z: number }>>> = {
    home: [[], [], []],
    away: [[], [], []],
  };
  for (const p of points) {
    if (p.team !== 'home' && p.team !== 'away') continue;
    const b = bandFor(p.z);
    buckets[p.team][b]!.push({ x: p.x, z: p.z });
  }

  const out: TeamShapeBand[] = [];
  for (const team of ['home', 'away'] as const) {
    for (const band of buckets[team]) {
      if (band.length < 2) continue;
      band.sort((a, b) => a.x - b.x);
      out.push({ team, points: band });
    }
  }
  return out;
}

/**
 * Compute the home-vs-away contact line ("frontier") for the Voronoi
 * tessellation of the input points. Returns line segments along grid-
 * cell edges where one side is owned by a home player and the other by
 * an away player. Same grid + ownership pass as {@link computeVoronoiCells}
 * so the frontier is exact relative to the cells the renderer shows.
 *
 * Segments are returned in world metres, axis-aligned (every segment
 * runs either along the x-axis or the z-axis since they trace grid-
 * cell edges). The renderer is free to stitch adjacent collinear
 * segments into a single path for visual smoothing — the raw output
 * here is intentionally simple.
 *
 * Returns an empty array when one or both teams have no players, or
 * when the input is empty.
 */
export function computeVoronoiFrontier(
  points: VoronoiPoint[],
  pitchLengthM: number,
  pitchWidthM: number,
): VoronoiFrontierSegment[] {
  if (points.length === 0) return [];
  // Cheap pre-check: if either team is missing we have no contact line.
  let hasHome = false;
  let hasAway = false;
  for (const p of points) {
    if (p.team === 'home') hasHome = true;
    else if (p.team === 'away') hasAway = true;
    if (hasHome && hasAway) break;
  }
  if (!hasHome || !hasAway) return [];

  const halfL = pitchLengthM / 2;
  const halfW = pitchWidthM / 2;
  const step = GRID_STEP_M;
  const nCols = Math.max(1, Math.round(pitchLengthM / step));
  const nRows = Math.max(1, Math.round(pitchWidthM / step));

  // Sample the same grid + nearest-neighbour ownership as
  // computeVoronoiCells. We only store the OWNING team here, not the
  // point index, since the frontier only needs to compare team labels
  // between adjacent samples.
  type Team = 'home' | 'away' | 'neutral';
  const teams: Team[] = new Array(nRows * nCols);
  for (let r = 0; r < nRows; r++) {
    const cx = -halfW + (r + 0.5) * step;
    for (let c = 0; c < nCols; c++) {
      const cz = -halfL + (c + 0.5) * step;
      let bestI = 0;
      let bestD = Infinity;
      for (let i = 0; i < points.length; i++) {
        const dx = points[i]!.x - cx;
        const dz = points[i]!.z - cz;
        const d = dx * dx + dz * dz;
        if (d < bestD) {
          bestD = d;
          bestI = i;
        }
      }
      teams[r * nCols + c] = (points[bestI]!.team ?? 'neutral') as Team;
    }
  }

  // Walk every neighbour pair once. East = (r, c) vs (r, c+1) — shared
  // edge runs along the x-axis at z = -halfL + (c+1)*step. South =
  // (r, c) vs (r+1, c) — shared edge runs along the z-axis at
  // x = -halfW + (r+1)*step.
  //
  // We emit a segment only when one side is home and the other is
  // away (neutral-vs-anything is not a "frontier" in the tactical sense).
  const segments: VoronoiFrontierSegment[] = [];
  const isContact = (a: Team, b: Team): boolean =>
    (a === 'home' && b === 'away') || (a === 'away' && b === 'home');
  for (let r = 0; r < nRows; r++) {
    for (let c = 0; c < nCols; c++) {
      const here = teams[r * nCols + c]!;
      // East neighbour
      if (c + 1 < nCols) {
        const east = teams[r * nCols + (c + 1)]!;
        if (isContact(here, east)) {
          const z = -halfL + (c + 1) * step;
          const x1 = -halfW + r * step;
          const x2 = -halfW + (r + 1) * step;
          segments.push({ a: { x: x1, z }, b: { x: x2, z } });
        }
      }
      // South neighbour (next row along x)
      if (r + 1 < nRows) {
        const south = teams[(r + 1) * nCols + c]!;
        if (isContact(here, south)) {
          const x = -halfW + (r + 1) * step;
          const z1 = -halfL + c * step;
          const z2 = -halfL + (c + 1) * step;
          segments.push({ a: { x, z: z1 }, b: { x, z: z2 } });
        }
      }
    }
  }
  return segments;
}

/**
 * Build a polygon for the sample cells owned by input index `idx`.
 *
 * Approach: collect every grid corner that touches at least one owned
 * sample cell, then compute the convex hull of those corners. For the
 * convex-hull-of-sample-cells case the result is exact; for non-
 * convex regions the hull approximates the boundary (acceptable for
 * an analytical overlay where the cells of 22 players on a soccer
 * pitch are almost always convex regions of the Voronoi diagram).
 *
 * Returns `[]` if the bucket is empty.
 */
function traceCellPolygon(
  ownership: Int32Array,
  idx: number,
  nRows: number,
  nCols: number,
  step: number,
  halfL: number,
  halfW: number,
): { x: number; z: number }[] {
  // Collect the four corners of every owned sample cell, dedup via a
  // Set keyed by integer (col, row) corner indices.
  const cornerSet = new Set<number>();
  let hasAny = false;

  for (let r = 0; r < nRows; r++) {
    for (let c = 0; c < nCols; c++) {
      if (ownership[r * nCols + c] !== idx) continue;
      hasAny = true;
      // Four grid-corner indices around the (c, r) sample cell.
      // Corner key: corner_c * (nRows + 1) + corner_r.
      // The grid corners run from (c=0, r=0) to (c=nCols, r=nRows).
      const cc0 = c;
      const cc1 = c + 1;
      const cr0 = r;
      const cr1 = r + 1;
      cornerSet.add(cc0 * (nRows + 1) + cr0);
      cornerSet.add(cc1 * (nRows + 1) + cr0);
      cornerSet.add(cc0 * (nRows + 1) + cr1);
      cornerSet.add(cc1 * (nRows + 1) + cr1);
    }
  }

  if (!hasAny) return [];

  // Materialise corner points in world metres. Corner (cc, cr) sits at
  // world (x = -halfW + cr*step, z = -halfL + cc*step).
  const pts: { x: number; z: number }[] = [];
  for (const key of cornerSet) {
    const cc = Math.floor(key / (nRows + 1));
    const cr = key - cc * (nRows + 1);
    pts.push({ x: -halfW + cr * step, z: -halfL + cc * step });
  }

  return convexHull(pts);
}

/**
 * Standard monotone-chain convex hull. Returns points in
 * counter-clockwise order. Output may be smaller than input when
 * colinear or duplicate points are present — those are still inside
 * the hull so the polygon contour is unaffected.
 */
function convexHull(
  input: { x: number; z: number }[],
): { x: number; z: number }[] {
  if (input.length < 3) return input.slice();
  // Sort by x then z (in our coord system either axis works; we use
  // (z, x) so the sweep direction is along the pitch length).
  const sorted = input
    .slice()
    .sort((a, b) => (a.z === b.z ? a.x - b.x : a.z - b.z));

  const cross = (
    o: { x: number; z: number },
    a: { x: number; z: number },
    b: { x: number; z: number },
  ): number => (a.z - o.z) * (b.x - o.x) - (a.x - o.x) * (b.z - o.z);

  const lower: { x: number; z: number }[] = [];
  for (const p of sorted) {
    while (
      lower.length >= 2 &&
      cross(lower[lower.length - 2]!, lower[lower.length - 1]!, p) <= 0
    ) {
      lower.pop();
    }
    lower.push(p);
  }
  const upper: { x: number; z: number }[] = [];
  for (let i = sorted.length - 1; i >= 0; i--) {
    const p = sorted[i]!;
    while (
      upper.length >= 2 &&
      cross(upper[upper.length - 2]!, upper[upper.length - 1]!, p) <= 0
    ) {
      upper.pop();
    }
    upper.push(p);
  }
  // Drop the last point of each half — it's the starting point of the
  // other half.
  lower.pop();
  upper.pop();
  return lower.concat(upper);
}

// ---------------------------------------------------------------------------
// Pass network
// ---------------------------------------------------------------------------

/**
 * Input to the pass-network computation. Coordinates are world metres,
 * pitch-centred — same convention as {@link VoronoiPoint}.
 *
 * In the broadcast-graphic version of this overlay (Sky MNF /
 * NFL telestrator) each node would be an event-data centroid and edges
 * would be weighted by completed-pass count. We don't have event data
 * on the tactics board yet, so the v1 weight proxy is purely spatial:
 * shorter same-team distance → stronger connection. The shape of the
 * API matches the eventual event-driven version so the renderer
 * doesn't need to change when real pass counts arrive.
 */
export interface PassNetPoint {
  x: number;
  z: number;
  team?: 'home' | 'away';
}

/** One edge in the pass network — a same-team pair plus the computed
 *  weight in [0, 1]. */
export interface PassNetEdge {
  from: PassNetPoint;
  to: PassNetPoint;
  team: 'home' | 'away' | undefined;
  /** Connection strength in (0, 1]. Closer pairs score higher. */
  weight: number;
}

/** Options for {@link computePassNetwork}. */
export interface PassNetOptions {
  /** Drop edges whose weight falls at-or-below this threshold so the
   *  graph stays legible. Default 0.05 — at the `1 / (1 + d)` proxy
   *  that's roughly "edges out to ~19 m". */
  minWeight?: number;
}

const DEFAULT_PASSNET_MIN_WEIGHT = 0.05;

/**
 * Build the same-team pass-network edge set for the input points.
 *
 * Every same-team pair contributes one edge with weight
 * `1 / (1 + distance_metres)`. Cross-team pairs are never included.
 * Edges with `weight <= minWeight` are filtered out so the rendered
 * graph stays legible (a fully-connected 11-node graph is 55 edges
 * which paints as a hairball; the threshold prunes the long-range
 * connections that carry the least information).
 *
 * Complexity: O(N²) — fine for N ≤ ~22 on a tactics board, no spatial
 * index needed.
 */
export function computePassNetwork(
  points: PassNetPoint[],
  opts: PassNetOptions = {},
): PassNetEdge[] {
  if (points.length < 2) return [];

  const minWeight = opts.minWeight ?? DEFAULT_PASSNET_MIN_WEIGHT;
  const edges: PassNetEdge[] = [];

  for (let i = 0; i < points.length; i++) {
    const a = points[i]!;
    for (let j = i + 1; j < points.length; j++) {
      const b = points[j]!;
      // Cross-team pairs never connect. Untagged points (team ===
      // undefined) only connect to other untagged points so a
      // partially-tagged input doesn't produce mystery edges.
      if (a.team !== b.team) continue;
      const dx = a.x - b.x;
      const dz = a.z - b.z;
      const dist = Math.sqrt(dx * dx + dz * dz);
      const weight = 1 / (1 + dist);
      if (weight <= minWeight) continue;
      edges.push({ from: a, to: b, team: a.team, weight });
    }
  }

  return edges;
}

// ---------------------------------------------------------------------------
// Pitch Control
// ---------------------------------------------------------------------------

/**
 * Pitch Control — a continuous probability surface across the pitch
 * answering "if a ball arrived here, who'd reach it first?"
 *
 * Reference: Spearman (2018) "Beyond Expected Goals" — modern broadcast
 * tactical overlays (Sky Sports / NBC Premier League analytics) lean on
 * exactly this primitive. It's a weighted upgrade over a Voronoi
 * diagram: instead of a hard nearest-neighbour partition, every player
 * contributes a soft "ownership" share that falls off with time-to-arrive.
 *
 * Algorithm — soft inverse-time weighting:
 *   1. Rasterise the pitch at `step` metres (default 2 m — 4× coarser
 *      than Voronoi's 1 m to keep render-side `<rect>` count manageable).
 *   2. For every cell centre, compute time-to-arrive per player:
 *        t_i = distance(player_i, cell) / speed_i
 *      Default speed: 7.5 m/s (mid-range outfielder peak running speed,
 *      a standard assumption in academic pitch-control literature).
 *   3. Sum inverse times per team:
 *        S_home = Σ 1 / t_i   for i ∈ home
 *        S_away = Σ 1 / t_i   for i ∈ away
 *   4. Cell ownership share for home:
 *        home_share = S_home / (S_home + S_away)
 *      ∈ [0, 1]. With only one team present the share clamps to 1 (or
 *      0 if only the away side has players). Empty input returns a
 *      zero grid.
 *
 * Grid-size rationale: at step=2m a standard pitch grid is ~34×53 ≈
 * 1.8k cells × ≤22 points = ~40k ops per frame. The render side emits
 * one `<rect>` per cell which the browser handles comfortably. Drop to
 * step=1m only if a consumer demonstrates the need — ~7k cells push
 * the SVG cost up substantially.
 */

/** A single contributing player. `team` is required because the
 *  ownership share has no meaning without sides; `speedMps` is
 *  optional with a sensible 7.5 m/s default. */
export interface PitchControlPoint {
  x: number;
  z: number;
  team: 'home' | 'away';
  /** Player's reachable speed in m/s — used for time-to-arrive. Defaults
   *  to 7.5 m/s, roughly the average outfielder peak-running speed used
   *  in academic pitch-control literature. */
  speedMps?: number;
}

/** Output grid. `homeShare` is row-major (`homeShare[r*nCols + c]`)
 *  with values in [0, 1] — away share is `1 - homeShare`. */
export interface PitchControlGrid {
  /** Number of rows (along x / width). */
  nRows: number;
  /** Number of cols (along z / length). */
  nCols: number;
  /** Cell size in metres. */
  step: number;
  /** Half pitch length (z half-extent). */
  halfL: number;
  /** Half pitch width (x half-extent). */
  halfW: number;
  /** Home team's expected ownership share at each cell, ∈ [0, 1].
   *  Float32Array for compact memory and fast typed-array access. */
  homeShare: Float32Array;
}

/** Options for {@link computePitchControl}. */
export interface PitchControlOptions {
  /** Grid resolution in metres. Default 2 (≈1.8k cells on a standard
   *  pitch). Halving to 1 m quadruples the cell count. */
  step?: number;
  /** Override the default player speed (m/s). Per-point `speedMps`
   *  takes precedence when set. */
  defaultSpeedMps?: number;
}

/** Default reachable speed in m/s — average outfielder sprint capacity. */
const DEFAULT_PITCH_CONTROL_SPEED = 7.5;

/** Default grid step in metres — 2 m, 4× coarser than the Voronoi
 *  grid because pitch control renders one rect per cell and the SVG
 *  cost dominates the algorithm cost. */
const DEFAULT_PITCH_CONTROL_STEP = 2;

/**
 * Compute the pitch-control grid for the given input points.
 *
 * Coordinate convention matches {@link computeVoronoiCells}:
 *   x ∈ [-pitchWidthM/2,  +pitchWidthM/2]
 *   z ∈ [-pitchLengthM/2, +pitchLengthM/2]
 *
 * Empty input returns a zero grid (all `homeShare` cells = 0 — i.e.
 * neither team has any expected control).
 */
export function computePitchControl(
  points: PitchControlPoint[],
  pitchLengthM: number,
  pitchWidthM: number,
  opts?: PitchControlOptions,
): PitchControlGrid {
  const step = opts?.step ?? DEFAULT_PITCH_CONTROL_STEP;
  const defaultSpeed = opts?.defaultSpeedMps ?? DEFAULT_PITCH_CONTROL_SPEED;

  const halfL = pitchLengthM / 2;
  const halfW = pitchWidthM / 2;

  const nCols = Math.max(1, Math.round(pitchLengthM / step)); // along z
  const nRows = Math.max(1, Math.round(pitchWidthM / step));  // along x

  const homeShare = new Float32Array(nRows * nCols);

  if (points.length === 0) {
    // Zero grid — no contributors means no expected control on either side.
    return { nRows, nCols, step, halfL, halfW, homeShare };
  }

  // Pre-extract per-point fields so the hot loop avoids object access.
  const pxArr = new Float32Array(points.length);
  const pzArr = new Float32Array(points.length);
  // invSpeed = 1/speed — multiplying by it gives time-to-arrive without
  // a divide inside the hot loop. (We still do one divide per cell on
  // `denom`, which is unavoidable.)
  const invSpeedArr = new Float32Array(points.length);
  const teamArr = new Int8Array(points.length); // 0 = home, 1 = away
  for (let i = 0; i < points.length; i++) {
    const p = points[i]!;
    pxArr[i] = p.x;
    pzArr[i] = p.z;
    const sp = p.speedMps ?? defaultSpeed;
    invSpeedArr[i] = sp > 0 ? 1 / sp : 1 / defaultSpeed;
    teamArr[i] = p.team === 'away' ? 1 : 0;
  }

  // Tiny epsilon so a player sitting exactly on a cell centre doesn't
  // produce 1/0. 1e-4 metres = 0.1 mm — negligible relative to the 2 m
  // grid step but enough to dodge the singularity cleanly.
  const EPS = 1e-4;

  for (let r = 0; r < nRows; r++) {
    const cx = -halfW + (r + 0.5) * step;
    for (let c = 0; c < nCols; c++) {
      const cz = -halfL + (c + 0.5) * step;
      let sumHome = 0;
      let sumAway = 0;
      for (let i = 0; i < points.length; i++) {
        const dx = pxArr[i]! - cx;
        const dz = pzArr[i]! - cz;
        const dist = Math.sqrt(dx * dx + dz * dz);
        // t = dist * invSpeed; inv_t = 1 / (t + EPS).
        const t = dist * invSpeedArr[i]!;
        const invT = 1 / (t + EPS);
        if (teamArr[i]! === 0) sumHome += invT;
        else sumAway += invT;
      }
      const denom = sumHome + sumAway;
      homeShare[r * nCols + c] = denom > 0 ? sumHome / denom : 0;
    }
  }

  return { nRows, nCols, step, halfL, halfW, homeShare };
}

// ---------------------------------------------------------------------------
// Offside line
// ---------------------------------------------------------------------------

/**
 * Input to {@link computeOffsideLines}. Coordinates are world metres,
 * pitch-centred — same convention as {@link VoronoiPoint}. The
 * `isGoalkeeper` flag excludes the keeper from the second-to-last
 * defender calculation; an absent flag is treated as `false` (outfielder).
 */
export interface OffsideInputPoint {
  x: number;
  z: number;
  team: 'home' | 'away';
  isGoalkeeper?: boolean;
}

/**
 * Per-team offside line position along the pitch's z-axis. `null` when
 * a team has fewer than 2 outfielders (no second-to-last defender
 * exists). The line spans the full width of the pitch — only the z
 * coordinate matters.
 */
export interface OffsideLines {
  homeLineZ: number | null;
  awayLineZ: number | null;
}

/**
 * Compute the Hawk-Eye-style offside lines for each team.
 *
 * For a team that defends a goal, the offside line is the z-coordinate
 * of the second-to-last *defender* — i.e. the second-deepest outfielder
 * measured from that team's own goal. Goalkeepers are excluded so a
 * keeper hanging back doesn't drag the line into the box.
 *
 * Convention from `TacticsLayout` (see ./types.ts):
 *   - home defends +z (their own goal sits at +halfL)
 *   - away defends -z (their own goal sits at -halfL)
 *
 * So:
 *   - home's "deepest" outfielders have the HIGHEST z; the offside
 *     line is the SECOND-highest z among home outfielders.
 *   - away's "deepest" outfielders have the LOWEST z; the offside
 *     line is the SECOND-lowest z among away outfielders.
 *
 * Returns `{ homeLineZ: null, awayLineZ: null }` for empty input.
 * Either side independently returns null when there are fewer than 2
 * eligible outfielders for that team.
 */
export function computeOffsideLines(
  points: OffsideInputPoint[],
): OffsideLines {
  const homeOutfielderZs: number[] = [];
  const awayOutfielderZs: number[] = [];
  for (const p of points) {
    if (p.isGoalkeeper) continue;
    if (p.team === 'home') homeOutfielderZs.push(p.z);
    else if (p.team === 'away') awayOutfielderZs.push(p.z);
  }

  let homeLineZ: number | null = null;
  if (homeOutfielderZs.length >= 2) {
    // Home defends +z — deepest from own goal is highest z. Sort
    // descending so [0] is deepest, [1] is the second-to-last defender.
    const sorted = homeOutfielderZs.slice().sort((a, b) => b - a);
    homeLineZ = sorted[1]!;
  }

  let awayLineZ: number | null = null;
  if (awayOutfielderZs.length >= 2) {
    // Away defends -z — deepest from own goal is lowest z. Sort
    // ascending so [0] is deepest, [1] is the second-to-last defender.
    const sorted = awayOutfielderZs.slice().sort((a, b) => a - b);
    awayLineZ = sorted[1]!;
  }

  return { homeLineZ, awayLineZ };
}

// ---------------------------------------------------------------------------
// Defensive shape polygon
// ---------------------------------------------------------------------------

/**
 * Input to {@link computeDefensiveShape}. Same shape as
 * {@link OffsideInputPoint} — goalkeepers are excluded from the hull so
 * the keeper standing on the goal line doesn't stretch the polygon to
 * the back of the net.
 */
export interface ShapeInputPoint {
  x: number;
  z: number;
  team: 'home' | 'away';
  isGoalkeeper?: boolean;
}

/** A defensive shape polygon — convex hull of the team's back-six
 *  outfielders. Empty `hull` array when there are too few eligible
 *  points to form a triangle. */
export interface DefensiveShape {
  hull: { x: number; z: number }[];
}

/** Number of "deepest" outfielders to include in the defensive block.
 *  Six is the back four + a holding pair — the canonical NFL-style
 *  coverage-shell adapted for soccer. If a team plays a back five with
 *  one screening midfielder, this still captures the same block. If a
 *  team only has 5 outfielders on the pitch, we'll trace the hull of
 *  those 5 instead (rather than refusing to draw anything). */
const DEFENSIVE_BLOCK_SIZE = 6;

/**
 * Compute the convex-hull polygon of a team's defensive block — the
 * deepest {@link DEFENSIVE_BLOCK_SIZE} outfielders measured from that
 * team's own goal.
 *
 * Goalkeepers are excluded (same rationale as the offside line). When
 * fewer than 3 eligible outfielders are present the hull is empty —
 * a polygon needs at least 3 vertices to render.
 *
 * Convention matches {@link computeOffsideLines}:
 *   - home defends +z, so "back-to-front from own goal" sorts z DESC.
 *   - away defends -z, so "back-to-front from own goal" sorts z ASC.
 *
 * The returned `hull` is the convex hull of the back-six (or all
 * eligible outfielders, whichever is smaller). Vertex order is the
 * same CCW winding produced by {@link convexHull}.
 */
export function computeDefensiveShape(
  points: ShapeInputPoint[],
  team: 'home' | 'away',
): DefensiveShape {
  const outfielders = points.filter(
    (p) => p.team === team && !p.isGoalkeeper,
  );
  if (outfielders.length < 3) return { hull: [] };

  // Sort back-to-front from team's own goal.
  const sorted = outfielders.slice().sort((a, b) =>
    team === 'home' ? b.z - a.z : a.z - b.z,
  );
  const block = sorted
    .slice(0, DEFENSIVE_BLOCK_SIZE)
    .map((p) => ({ x: p.x, z: p.z }));

  if (block.length < 3) return { hull: [] };
  return { hull: convexHull(block) };
}

// ---------------------------------------------------------------------------
// Heatmap (player density / territory)
// ---------------------------------------------------------------------------

/**
 * Input to the heatmap computation. Coordinates are world metres,
 * pitch-centred (same convention as `VoronoiPoint`).
 */
export interface HeatmapPoint {
  x: number;
  z: number;
  team?: 'home' | 'away';
}

/**
 * Output grid of the heatmap. The grid runs row-major over the pitch
 * rectangle (centred on origin):
 *   r ∈ [0, nRows): along the pitch width (x axis)
 *   c ∈ [0, nCols): along the pitch length (z axis)
 *
 * Each grid cell centre sits at:
 *   x = -halfW + (r + 0.5) * step
 *   z = -halfL + (c + 0.5) * step
 *
 * `intensity` is a row-major Float32Array of length `nRows * nCols`
 * with values in [0, 1] — normalised so the maximum value equals
 * exactly 1.0 (or 0 throughout if no points were supplied / all
 * points filtered out by the optional team filter).
 *
 * `team` echoes the filter applied by the caller via `opts.team`.
 * Defaults to `'all'` when no filter was passed. Renderers can use
 * this to colour multi-team heatmaps differently or to label legends.
 */
export interface HeatmapGrid {
  nRows: number;
  nCols: number;
  step: number;
  halfL: number;
  halfW: number;
  intensity: Float32Array;
  team: 'home' | 'away' | 'all';
}

/** Options accepted by {@link computeHeatmap}. */
export interface HeatmapOptions {
  /**
   * Gaussian kernel σ in metres. Larger values produce smoother,
   * broader hot zones; smaller values produce tighter peaks at each
   * input point. Default 6 m — wide enough to suggest a player's
   * influence zone, tight enough to keep overload spotting useful.
   */
  sigma?: number;
  /**
   * Restrict to one team's points only. Untagged points are excluded
   * when this is set to a specific team. Defaults to `'all'`.
   */
  team?: 'home' | 'away' | 'all';
}

/** Heatmap grid step in metres. 2 m gives a 35×53 = ~1.8k cell grid on
 *  a standard pitch — coarser than Voronoi's 1 m sampler (which is
 *  doing nearest-point classification, not kernel summation) because
 *  the Gaussian is smooth and a 2 m sampler still captures the
 *  resulting hot zones faithfully. */
const HEATMAP_GRID_STEP_M = 2;

/** Default Gaussian σ in metres — see {@link HeatmapOptions.sigma}. */
const DEFAULT_HEATMAP_SIGMA_M = 6;

/**
 * Compute a Gaussian-KDE heatmap of player density across the pitch.
 *
 * For every grid-cell centre, sum `exp(-(d² / (2 σ²)))` from every
 * input point where d is the Euclidean distance to that input point.
 * The resulting field is then normalised so the maximum cell value
 * equals 1.0 — coaches read it relatively (warm vs cool), not as an
 * absolute density.
 *
 * Edge cases:
 *  - `points = []` → zero-filled grid (no normalisation pass).
 *  - `opts.team` filters before summing; if no points survive the
 *    filter, the grid is also zero-filled.
 *  - `opts.sigma` ≤ 0 falls back to the default. NaN / Infinity
 *    likewise.
 *
 * Performance: 22 points × 35×53 grid = ~40k exp() evaluations.
 * Comfortably under 5 ms on a Pixel-class device.
 */
export function computeHeatmap(
  points: HeatmapPoint[],
  pitchLengthM: number,
  pitchWidthM: number,
  opts: HeatmapOptions = {},
): HeatmapGrid {
  const halfL = pitchLengthM / 2;
  const halfW = pitchWidthM / 2;
  const step = HEATMAP_GRID_STEP_M;
  const nCols = Math.max(1, Math.round(pitchLengthM / step)); // along z
  const nRows = Math.max(1, Math.round(pitchWidthM / step)); // along x

  // Normalise team filter — anything other than 'home' / 'away'
  // collapses to 'all' so callers passing undefined or a stray string
  // get the unfiltered behaviour.
  const teamFilter: 'home' | 'away' | 'all' =
    opts.team === 'home' || opts.team === 'away' ? opts.team : 'all';

  // Apply the optional team filter up front so the hot inner loop
  // doesn't need to branch on every point.
  const filtered: HeatmapPoint[] =
    teamFilter === 'all'
      ? points
      : points.filter((p) => p.team === teamFilter);

  const intensity = new Float32Array(nRows * nCols);

  // Empty input → zero grid (no normalisation pass).
  if (filtered.length === 0) {
    return { nRows, nCols, step, halfL, halfW, intensity, team: teamFilter };
  }

  // Resolve σ. Non-finite or non-positive falls back to default.
  const sigmaRaw = opts.sigma;
  const sigma =
    typeof sigmaRaw === 'number' && Number.isFinite(sigmaRaw) && sigmaRaw > 0
      ? sigmaRaw
      : DEFAULT_HEATMAP_SIGMA_M;
  const twoSigmaSq = 2 * sigma * sigma;

  // Accumulate Gaussian contributions. Indexing: row * nCols + col.
  let maxVal = 0;
  for (let r = 0; r < nRows; r++) {
    const cx = -halfW + (r + 0.5) * step;
    for (let c = 0; c < nCols; c++) {
      const cz = -halfL + (c + 0.5) * step;
      let sum = 0;
      for (let i = 0; i < filtered.length; i++) {
        const p = filtered[i]!;
        const dx = p.x - cx;
        const dz = p.z - cz;
        const d2 = dx * dx + dz * dz;
        sum += Math.exp(-d2 / twoSigmaSq);
      }
      intensity[r * nCols + c] = sum;
      if (sum > maxVal) maxVal = sum;
    }
  }

  // Normalise so the peak is exactly 1.0. Guard against the
  // degenerate maxVal === 0 case (e.g. NaN inputs that all collapse
  // to 0); in that case leave the grid as-is.
  if (maxVal > 0) {
    for (let k = 0; k < intensity.length; k++) {
      intensity[k] = intensity[k]! / maxVal;
    }
  }

  return { nRows, nCols, step, halfL, halfW, intensity, team: teamFilter };
}

